import React, { createContext, useContext, useMemo } from "react"
import { where } from "firebase/firestore"
import { ILocationItem } from "react-ui-components/components/models/ILocation"
import { IRaffle } from "react-ui-components/components/models/IRaffle"
import { useLocation } from "react-ui-components/components/location/locationContext"
import { useNamespacedTranslation } from "../hooks/useNamespacedTranslation"
import { usePaginatedFirestoreList, type RawDoc } from "../hooks/usePaginatedFirestoreList"
import { overlayRaffleAssignments, resolveRaffleAssignments } from "../contentAssignments"
import { isRaffleZipAllowed } from "./raffleZipAccess"
import { isVisibleRaffle } from "./raffleVisibility"

interface RaffleContextProps {
  raffles: IRaffle[]
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  error: string | null
  fetchNextPage: () => void
  refreshRaffles: () => void
  setRaffles?: React.Dispatch<React.SetStateAction<IRaffle[]>>
}

const RaffleContext = createContext<RaffleContextProps | undefined>(undefined)
const RAFFLE_PAGE_SIZE = 10

const matchesLocation = (raffleLocation: any, selected: ILocationItem) => {
  const normalize = (val?: string) => (val || "").trim()
  const matchesField = (value: string, target: string) => {
    if (!target) return true
    const normalizedValue = normalize(value)
    return normalizedValue === target || normalizedValue === "" || normalizedValue === "*"
  }

  const selectedCountry = normalize(selected.country)
  const selectedProvince = normalize(selected.state)
  const selectedCity = normalize(selected.city)

  const country = normalize(raffleLocation?.country)
  const province = normalize(raffleLocation?.province || raffleLocation?.state)
  const city = normalize(raffleLocation?.city)

  if (!selectedCountry && !selectedProvince && !selectedCity) {
    return true
  }

  return (
    matchesField(country, selectedCountry) &&
    matchesField(province, selectedProvince) &&
    matchesField(city, selectedCity)
  )
}

const validateRaffles = (docs: RawDoc[]): IRaffle[] =>
  docs.filter((doc): doc is RawDoc & IRaffle => Boolean(doc) && typeof doc.id === "string") as IRaffle[]

export const RaffleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentLocation } = useLocation()
  const { t } = useNamespacedTranslation(["common", "error"])

  const isValidLocation = Boolean(currentLocation)
  const country = currentLocation?.country?.trim()
  // `zip` isn't on the currently published ILocationItem type yet - cast until the
  // react-ui-components dependency pin picks up the ZIP work.
  const userZip = (currentLocation as { zip?: string } | undefined)?.zip?.trim()

  const queryConstraints = useMemo(() => {
    const conditions = [where("disabled", "==", false)]
    if (country) {
      // Only one "in" filter is allowed; state/city filtering happens client-side below.
      conditions.push(where("location.country", "in", [country, "", "*"]))
    }
    return conditions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country])

  const transformPage = useMemo(
    () => async (rawDocs: RawDoc[]): Promise<IRaffle[]> => {
      const validatedRaffles = validateRaffles(rawDocs).filter(
        (raffle) =>
          isVisibleRaffle(raffle) &&
          matchesLocation((raffle as any).location, currentLocation) &&
          isRaffleZipAllowed(raffle as any, userZip),
      )
      const resolvedAssignmentsByRaffleId = await resolveRaffleAssignments(
        validatedRaffles.map((raffle) => raffle.id),
      )
      return validatedRaffles.map((raffle) =>
        overlayRaffleAssignments(raffle, resolvedAssignmentsByRaffleId[raffle.id]),
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLocation?.country, currentLocation?.state, currentLocation?.city, userZip],
  )

  const {
    items: raffles,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    fetchNextPage,
    refresh,
    setItems: setRaffles,
  } = usePaginatedFirestoreList<IRaffle>({
    queryKey: [
      "raffles",
      country ?? null,
      currentLocation?.state ?? null,
      currentLocation?.city ?? null,
      userZip ?? null,
    ],
    collectionPath: "raffles",
    queryConstraints,
    orderByField: "order",
    pageSize: RAFFLE_PAGE_SIZE,
    transformPage,
    liveFirstPage: true,
    enabled: isValidLocation,
  })

  const errorMessage = !isValidLocation
    ? t("invalidLocation")
    : error
      ? t("loadRaffles")
      : null

  return (
    <RaffleContext.Provider
      value={{
        raffles,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        error: errorMessage,
        fetchNextPage,
        refreshRaffles: refresh,
        setRaffles,
      }}
    >
      {children}
    </RaffleContext.Provider>
  )
}

export const useRaffleContext = () => {
  const context = useContext(RaffleContext)
  if (!context) {
    throw new Error("useRaffleContext must be used within a RaffleProvider")
  }
  return context
}
